import { FC, useEffect } from 'react';
import EditorImg from 'lowcode/components/EditorImg';
import styled from './index.module.scss';
import { useLang } from '@/app/utils/useLang';
import { BoostMinIcon, LinkMinIcon, QuietMinIcon, FusionMinIcon, OasisMinIcon, GuardMinIcon, StreamMinIcon } from '@/components/XPower/svg';
import { useGlobal } from '@/utils/useGlobal';
import getLocal from '@/app/utils/getLocal';
import { EU_LIST } from '@/components/XPower/constants';

const Safety: FC = () => {
    const { isMobile } = useGlobal();
    const { t } = useLang(`xcode.safely`);
    const { locale } = getLocal();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const offset = isMobile ? 10 : 30; // title与content间距
            const sceneA = AniContainer.create({
                asyncFromTo: [
                    // 1
                    [`.${styled.Safety} .intelligence .text`, { opacity: 0 }, { opacity: 1 }, 1],
                    [`.${styled.Safety} .performance .text`, { opacity: 0 }, { opacity: 1 }, 1],
                    [`.${styled.Safety} .safety .text`, { opacity: 0 }, { opacity: 1 }, 1],
                    [`.${styled.Safety} .safety .text`, { opacity: 1 }, { opacity: 1 }, 1],
                    // 2
                    [`.${styled.Safety} .intelligence .text`, { opacity: 1 }, { opacity: 0 }, 0.5],
                    [`.${styled.Safety} .safety .text`, { opacity: 1 }, { opacity: 0 }, 0.5],
                    [`.${styled.Safety} .bottom-desc`, { opacity: 1 }, { opacity: 0 }, 0.5],
                    [`.${styled.Safety} .performance .text`, { y: 0 }, { y: -200 }, 1],
                    [`.${styled.Safety} .performance .content`, { opacity: 0, y: 0 }, { opacity: 1, y: -200 + offset }, 1],
                    // 4.5
                    // [`.${styled.Safety} .performance .content`, { opacity: 1, y: -170 }, { opacity: 1, y: -170 }, 1],

                    // 3
                    [`.${styled.Safety} .performance .text`, { opacity: 1, y: -200 }, { opacity: 0, y: -400 }, 1],
                    [`.${styled.Safety} .performance .content`, { opacity: 1, y: -200 + offset }, { opacity: 0, y: -350 }, 1],

                    // 4
                    [`.${styled.Safety} .intelligence .text`, { opacity: 0, y: 120 }, { opacity: 1, y: 0 }, 1],
                    [`.${styled.Safety} .intelligence .content`, { opacity: 0, y: 120 + offset }, { opacity: 1, y: offset }, 1],
                    [`.${styled.Safety} .safety .text`, { opacity: 0, y: 120 }, { opacity: 1, y: 0 }, 1],
                    [`.${styled.Safety} .safety .content`, { opacity: 0, y: 120 + offset }, { opacity: 1, y: offset }, 1],
                    // 5
                    [`.${styled.Safety} .intelligence .text`, { opacity: 1, y: 0 }, { opacity: 0.7, y: -180 }, 1],
                    [`.${styled.Safety} .intelligence .content`, { opacity: 1, y: offset }, { opacity: 0.7, y: -180 + offset }, 1],
                    [`.${styled.Safety} .safety .text`, { opacity: 1, y: 0 }, { opacity: 0.7, y: -180 }, 1],
                    [`.${styled.Safety} .safety .content`, { opacity: 1, y: offset }, { opacity: 0.7, y: -180 + offset }, 1],
                ],
                asyncTimeLine: [1, 1, 1, 1.5, 2, 2, 2, 2.5, 2.5, 3.5, 3.5, 4.5, 4.5, 4.5, 4.5, 5.5, 5.5, 5.5, 5.5],
                triggerElement: `.${styled.Safety}`,
                triggerHook: 1,
                offset: '150%',
                duration: '170%',
            });
            AniContainer.scrollHandle(); //执行一次初始化
            return () => AniContainer.removeScene(sceneA);
        }
    }, []);

    const performContent = {
        intelligence: [
            {
                icon: 'EcoFlow Oasis',
                desc: t('oasis_desc'),
            },
        ],
        performance: [
            {
                icon: <StreamMinIcon />,
                desc: t('stream_desc'),
            },
            {
                icon: <BoostMinIcon />,
                desc: t('boost_desc'),
            },
            {
                icon: <LinkMinIcon />,
                desc: t('link_desc'),
            },
            {
                icon: <QuietMinIcon />,
                desc: t('quiet_desc'),
            },
            {
                icon: <FusionMinIcon />,
                desc: t('fusion_desc'),
            },
        ],
        safety: [
            {
                icon: <GuardMinIcon />,
                desc: t('guard_desc'),
            },
        ],
    };

    const getContent = (type: keyof typeof performContent) => {
        // eu的performance部分去掉x-link
        if (EU_LIST.includes(locale) && type === 'performance') performContent[type].splice(2, 1);
        return performContent[type].map((item: any, index: number) => <XDescItem icon={item.icon} desc={item.desc} key={`${type}${index}`} />);
    };

    const XDescItem = ({ icon, desc }: any) => (
        <>
            <div className="x-icon">{icon}</div>
            <div className="x-desc">{desc}</div>
        </>
    );

    return (
        <div className={styled.Safety}>
            <div className="sticky">
                <div className="safety-content">
                    <EditorImg className="img" pc="https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1716975734081/pc-safely-bg.png" alt={t('title')} />
                    <div className="perform-desc">
                        <div className="intelligence">
                            <div className="text">{t('intelligence')}</div>
                            <div className="content">{getContent('intelligence')}</div>
                        </div>
                        <div className="performance">
                            <div className="text">{t('performance')}</div>
                            <div className="content">{getContent('performance')}</div>
                        </div>
                        <div className="safety">
                            <div className="text">{t('safety')}</div>
                            <div className="content">{getContent('safety')}</div>
                        </div>
                    </div>
                </div>
                <div className="bottom-desc">
                    <div className="desc">{t('safety_desc')}</div>
                    <div className="tips">{t('safety_tip')}</div>
                </div>
            </div>
        </div>
    );
};

export default Safety;
